import { Component, type ReactNode, type ErrorInfo } from "react";
import { fontSize, fontWeight, radius, semanticColor } from "@/styles/tokens";

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  section?: string; // e.g., "chat", "calendar", "settings"
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Client-side error logger
function reportError(report: ErrorReport): void {
  // Log to console in structured format
  console.error("[Synqus Error Report]", JSON.stringify(report, null, 2));

  // Send to server (fire-and-forget)
  try {
    navigator.sendBeacon?.(
      "/api/v1/client-errors",
      JSON.stringify(report)
    );
  } catch {
    // Silently fail — error reporting should never cause errors
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ errorInfo: info });

    reportError({
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack || undefined,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const section = this.props.section;

      return (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            minHeight: section ? "200px" : "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 16,
            padding: 24,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48 }} aria-hidden="true">
            &#x26A0;&#xFE0F;
          </div>
          <h2 style={{ fontSize: fontSize["4xl"], fontWeight: fontWeight.bold, margin: 0 }}>
            {section ? `${section} encountered an error` : "Something went wrong"}
          </h2>
          <p
            style={{
              fontSize: fontSize.base,
              color: "#888",
              maxWidth: 400,
              margin: 0,
            }}
          >
            {this.state.error?.message || "An unexpected error occurred."}
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: "10px 24px",
                borderRadius: radius.lg,
                background: "#7C3AED",
                color: semanticColor.white,
                border: "none",
                cursor: "pointer",
                fontWeight: fontWeight.semibold,
                fontSize: fontSize.base,
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 24px",
                borderRadius: radius.lg,
                background: "transparent",
                color: "#7C3AED",
                border: "2px solid #7C3AED",
                cursor: "pointer",
                fontWeight: fontWeight.semibold,
                fontSize: fontSize.base,
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
